import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  KeyRound, Eye, EyeOff, Plus, Trash2, ShieldCheck, History, ExternalLink, Copy, X, Loader2,
  Star, CheckCircle2, Circle, Phone,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/CrudModule";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const SCOPES = [
  { value: "carrier", label: "Carrier" }, { value: "agency", label: "Agency" },
  { value: "quote_tool", label: "Quote Tool" }, { value: "crm", label: "CRM" },
  { value: "lead_vendor", label: "Lead Vendor" }, { value: "licensing", label: "Licensing" },
];
const STATUS_META = {
  active: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  pending_verification: "bg-[#B28A3D]/12 text-[#7a5d27] ring-[#B28A3D]/30",
  expired: "bg-amber-50 text-amber-700 ring-amber-200",
  disabled: "bg-slate-100 text-slate-500 ring-slate-200",
};
const STATUSES = ["pending_verification", "active", "expired", "disabled"];

export default function CredentialVault() {
  const { user } = useAuth();
  const canManage = ["admin", "ops_manager"].includes(user.role);
  const isAgent = user.role === "agent";
  const canWrite = (c) => canManage || c.owner_user_id === user.id;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState({});   // id -> {username, secret, link}
  const [filter, setFilter] = useState("");
  const [view, setView] = useState("essentials");
  const [editing, setEditing] = useState(null);    // cred or {} for new
  const [logFor, setLogFor] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/credentials").then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const reveal = async (id) => {
    if (revealed[id]) { setRevealed((r) => { const c = { ...r }; delete c[id]; return c; }); return; }
    try {
      const { data } = await api.get(`/credentials/${id}/reveal`);
      setRevealed((r) => ({ ...r, [id]: data }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this credential permanently?")) return;
    try { await api.delete(`/credentials/${id}`); toast.success("Credential deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const launch = async (c) => {
    try {
      const { data } = await api.post(`/credentials/${c.id}/launch`);
      window.open(data.link, "_blank", "noopener");
      toast.success("Launch logged");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const toggleEssential = async (c) => {
    try {
      await api.put(`/credentials/${c.id}`, { launch_essential: !c.launch_essential });
      toast.success(c.launch_essential ? "Removed from Launch Essentials" : "Added to Launch Essentials");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const verify = async (c) => {
    if (!window.confirm(`Mark "${c.label}" Verified? Do this only after a successful manual sign-in check.`)) return;
    try {
      await api.post(`/credentials/${c.id}/verify`);
      toast.success("Marked Verified");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const canVerify = (c) => canManage || c.owner_user_id === user.id;
  const searching = filter.trim().length > 0;
  const matched = items.filter((c) =>
    `${c.label} ${c.connects_to} ${c.scope_type}`.toLowerCase().includes(filter.toLowerCase()));
  const essentials = items.filter((c) => c.launch_essential);
  const filtered = searching ? matched : (view === "all" ? items : essentials);

  return (
    <div>
      <PageHeader title="Credential Vault" subtitle="Encrypted carrier, agency, quote-tool, CRM, lead-vendor & licensing logins. Masked by default; every reveal is logged." />

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="inline-flex rounded-lg border border-border bg-card p-0.5">
          <button onClick={() => setView("essentials")} data-testid="vault-view-essentials"
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${view === "essentials" && !searching ? "bg-[#17324D] text-white" : "text-muted-foreground hover:text-foreground"}`}>
            <Star className="inline h-3.5 w-3.5 mr-1 -mt-0.5" /> Launch Essentials
          </button>
          {(canManage || isAgent) && (
            <button onClick={() => setView("all")} data-testid="vault-view-all"
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${view === "all" && !searching ? "bg-[#17324D] text-white" : "text-muted-foreground hover:text-foreground"}`}>
              All ({items.length})
            </button>
          )}
        </div>
        <Input placeholder="Search all credentials by label, carrier, scope…" value={filter} onChange={(e) => setFilter(e.target.value)} className="max-w-xs" data-testid="vault-search" />
        <div className="flex-1" />
        {(canManage || isAgent) && <Button onClick={() => setEditing({})} data-testid="vault-add"><Plus className="mr-1.5 h-4 w-4" /> {isAgent ? "Add Carrier" : "Add Credential"}</Button>}
      </div>

      {!searching && view === "essentials" && (
        <LaunchEssentialsChecklist essentials={essentials} />
      )}

      <div className="flex items-start gap-2 rounded-lg border border-[#315A7D]/20 bg-[#315A7D]/5 px-4 py-2.5 text-[12px] text-[#2c5170] mb-4">
        <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" />
        <span>{searching
          ? `Searching all ${items.length} credentials. Results below.`
          : view === "essentials"
            ? "Daily view shows only Launch Essentials. All other systems stay Pending Verification and hidden until you search for them."
            : "Showing all credentials. Stored encrypted at rest and excluded from exports."}</span>
      </div>

      {loading ? (
        <div className="grid place-items-center py-16 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-card py-16 text-center" data-testid="vault-empty">
          <KeyRound className="mx-auto h-8 w-8 text-muted-foreground/50" />
          <p className="mt-3 text-sm text-muted-foreground">
            {searching ? "No credentials match your search."
              : view === "essentials" ? "No Launch Essentials yet. Search for a system above and tap the ☆ star to add it here."
              : "No credentials yet. Migrate from a workbook in Import & Review, or add one manually."}
          </p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((c) => {
            const rev = revealed[c.id];
            return (
              <div key={c.id} className="rounded-lg border border-border bg-card p-4" data-testid={`cred-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-heading font-semibold text-sm truncate">{c.label}</div>
                    <div className="text-[11px] text-muted-foreground capitalize">{c.scope_type.replace("_", " ")}{c.connects_to ? ` · ${c.connects_to}` : ""}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {canWrite(c) && (
                      <button onClick={() => toggleEssential(c)} title={c.launch_essential ? "Remove from Launch Essentials" : "Add to Launch Essentials"} data-testid={`essential-${c.id}`}>
                        <Star className={`h-4 w-4 ${c.launch_essential ? "fill-[#B28A3D] text-[#B28A3D]" : "text-muted-foreground/40 hover:text-[#B28A3D]"}`} />
                      </button>
                    )}
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset capitalize ${STATUS_META[c.status] || STATUS_META.disabled}`}>{c.status.replace("_", " ")}</span>
                  </div>
                </div>
                <div className="mt-3 space-y-1.5 text-[12px]">
                  <Field label="Username" value={rev ? rev.username : c.username_masked} mono />
                  <Field label="Password" value={rev ? rev.secret : (c.has_secret ? "••••••••" : "—")} mono />
                  {c.link && (
                    c.auto_launch_allowed ? (
                      <button onClick={() => launch(c)} className="inline-flex items-center gap-1 text-[#315A7D] hover:underline" data-testid={`launch-${c.id}`}>
                        <ExternalLink className="h-3 w-3" /> Launch portal <span className="text-[10px] text-emerald-600">(auto-launch on)</span>
                      </button>
                    ) : (
                      <a href={c.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-muted-foreground hover:text-[#315A7D] hover:underline truncate" title="Manual access — auto-launch disabled">
                        <ExternalLink className="h-3 w-3" /> Portal link (manual)
                      </a>
                    )
                  )}
                </div>
                <div className="mt-3 flex items-center gap-1.5 border-t border-border pt-2.5 flex-wrap">
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => reveal(c.id)} data-testid={`reveal-${c.id}`}>
                    {rev ? <><EyeOff className="mr-1 h-3.5 w-3.5" /> Hide</> : <><Eye className="mr-1 h-3.5 w-3.5" /> Reveal</>}
                  </Button>
                  {rev && <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => { navigator.clipboard?.writeText(rev.secret); toast.success("Password copied"); }}><Copy className="mr-1 h-3.5 w-3.5" /> Copy</Button>}
                  {c.status !== "active" && canVerify(c) && (
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-emerald-700 hover:text-emerald-800" onClick={() => verify(c)} data-testid={`verify-${c.id}`}>
                      <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Mark Verified
                    </Button>
                  )}
                  {(canManage || canWrite(c)) && <>
                    <div className="flex-1" />
                    {canManage && <Button size="sm" variant="ghost" className="h-7 px-1.5" onClick={() => setLogFor(c)} title="Access log"><History className="h-3.5 w-3.5" /></Button>}
                    {canWrite(c) && <Button size="sm" variant="ghost" className="h-7 px-1.5" onClick={() => setEditing(c)} title="Edit">···</Button>}
                    {canWrite(c) && <Button size="sm" variant="ghost" className="h-7 px-1.5 text-destructive" onClick={() => remove(c.id)} title="Delete"><Trash2 className="h-3.5 w-3.5" /></Button>}
                  </>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && <CredDialog cred={editing} isManager={canManage} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {logFor && <AccessLog cred={logFor} onClose={() => setLogFor(null)} />}
    </div>
  );
}

const ESSENTIAL_STEPS = [
  { label: "Primary agency / FMO portal", scopes: ["agency"] },
  { label: "Primary carrier portal", scopes: ["carrier"] },
  { label: "Main CRM / lead source", scopes: ["crm", "lead_vendor"] },
  { label: "Licensing / appointment portal", scopes: ["licensing"], optional: true },
];

function LaunchEssentialsChecklist({ essentials }) {
  const verifiedCount = essentials.filter((c) => c.status === "active").length;
  return (
    <div className="rounded-lg border border-[#B28A3D]/30 bg-[#B28A3D]/5 p-4 mb-4" data-testid="launch-essentials-checklist">
      <div className="flex items-center justify-between mb-3">
        <div className="font-heading font-semibold text-sm flex items-center gap-1.5"><Star className="h-4 w-4 fill-[#B28A3D] text-[#B28A3D]" /> Launch Essentials — first real workflow</div>
        <span className="text-[11px] text-muted-foreground">{verifiedCount}/{essentials.length} verified</span>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {ESSENTIAL_STEPS.map((step) => {
          const matches = essentials.filter((c) => step.scopes.includes(c.scope_type));
          const verified = matches.some((c) => c.status === "active");
          const has = matches.length > 0;
          return (
            <div key={step.label} className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-[12px]">
              {verified ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                : <Circle className={`h-4 w-4 shrink-0 ${has ? "text-[#B28A3D]" : "text-muted-foreground/40"}`} />}
              <span className="flex-1">{step.label}{step.optional && <span className="text-muted-foreground"> (if needed)</span>}</span>
              <span className={`text-[10px] font-medium ${verified ? "text-emerald-600" : has ? "text-[#7a5d27]" : "text-muted-foreground/60"}`}>
                {verified ? "Verified" : has ? "Pending" : "Not added"}
              </span>
            </div>
          );
        })}
        <div className="flex items-center gap-2 rounded-md border border-dashed border-border bg-card/60 px-3 py-2 text-[12px] text-muted-foreground sm:col-span-2">
          <Phone className="h-4 w-4 shrink-0" />
          <span className="flex-1">Twilio calling — configured separately in <strong className="text-foreground">Telephony → Owner/Admin Setup</strong>.</span>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className={`truncate max-w-[60%] ${mono ? "font-mono" : ""}`} title={value}>{value || "—"}</span>
    </div>
  );
}

function CredDialog({ cred, isManager, onClose, onSaved }) {
  const isNew = !cred.id;
  const [f, setF] = useState({
    label: cred.label || "", scope_type: cred.scope_type || "carrier", connects_to: cred.connects_to || "",
    link: cred.link || "", username: cred.username || "", secret: "", status: cred.status || "pending_verification",
    allowed_roles: cred.allowed_roles || ["admin", "ops_manager"], auto_launch_allowed: !!cred.auto_launch_allowed,
    launch_essential: !!cred.launch_essential,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!f.label) { toast.error("Label is required"); return; }
    setSaving(true);
    try {
      if (isNew) await api.post("/credentials", f);
      else {
        const upd = { ...f };
        if (!upd.secret) delete upd.secret;
        await api.put(`/credentials/${cred.id}`, upd);
      }
      toast.success(isNew ? "Credential added" : "Credential updated");
      onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="cred-dialog">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-xl bg-card p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <div className="font-heading text-lg font-bold flex items-center gap-2"><KeyRound className="h-5 w-5 text-[#B28A3D]" /> {isNew ? "Add Credential" : "Edit Credential"}</div>
          <button onClick={onClose}><X className="h-5 w-5 text-muted-foreground hover:text-foreground" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label className="text-xs">Label</Label><Input value={f.label} onChange={(e) => set("label", e.target.value)} data-testid="cred-label" /></div>
          <div><Label className="text-xs">Scope</Label>
            <Select value={f.scope_type} onValueChange={(v) => set("scope_type", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{SCOPES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Connects To</Label><Input value={f.connects_to} onChange={(e) => set("connects_to", e.target.value)} placeholder="Carrier / vendor" /></div>
          <div className="col-span-2"><Label className="text-xs">Portal Link</Label><Input value={f.link} onChange={(e) => set("link", e.target.value)} placeholder="https://…" /></div>
          <div><Label className="text-xs">Username</Label><Input value={f.username} onChange={(e) => set("username", e.target.value)} /></div>
          <div><Label className="text-xs">Password {isNew ? "" : "(leave blank to keep)"}</Label><Input type="password" value={f.secret} onChange={(e) => set("secret", e.target.value)} data-testid="cred-secret" /></div>
          <div><Label className="text-xs">Status</Label>
            <Select value={f.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {isManager && (
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-[12px] text-muted-foreground">
              <input type="checkbox" checked={f.allowed_roles.includes("agent")}
                onChange={(e) => set("allowed_roles", e.target.checked ? ["admin", "ops_manager", "agent"] : ["admin", "ops_manager"])} />
              Allow assigned agents
            </label>
          </div>
          )}
          <div className="col-span-2">
            <label className="flex items-center gap-2 text-[12px] text-[#7a5d27]">
              <input type="checkbox" checked={f.launch_essential}
                onChange={(e) => set("launch_essential", e.target.checked)} data-testid="cred-essential" />
              ⭐ Add to Launch Essentials (surfaces in the daily first-workflow checklist)
            </label>
          </div>
          <div className="col-span-2 rounded-lg border border-amber-200 bg-amber-50/60 p-3">
            <label className="flex items-start gap-2 text-[12px]">
              <input type="checkbox" className="mt-0.5" checked={f.auto_launch_allowed}
                onChange={(e) => set("auto_launch_allowed", e.target.checked)} data-testid="cred-autolaunch" />
              <span>
                <span className="font-medium text-amber-900">Allow auto-launch of the portal link from case workflow</span>
                <span className="block text-[11px] text-amber-800 mt-0.5">
                  Default OFF (manual access only). This only lets the platform <strong>open the approved portal link</strong> — no automatic login, autofill, or session automation. <strong>Confirm the carrier/vendor's terms and permissions allow this before enabling.</strong> Every change and every launch is logged.
                </span>
              </span>
            </label>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="cred-save">{saving ? "Saving…" : "Save"}</Button>
        </div>
      </div>
    </div>
  );
}

function AccessLog({ cred, onClose }) {
  const [log, setLog] = useState(null);
  useEffect(() => { api.get(`/credentials/${cred.id}/access-log`).then(({ data }) => setLog(data)).catch(() => setLog([])); }, [cred.id]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-md max-h-[80vh] overflow-auto rounded-xl bg-card p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-3">
          <div className="font-heading font-bold flex items-center gap-2"><History className="h-4 w-4" /> Access Log · {cred.label}</div>
          <button onClick={onClose}><X className="h-5 w-5 text-muted-foreground" /></button>
        </div>
        {!log ? <div className="py-8 text-center text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
          : log.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">No access recorded yet.</p>
          : <div className="space-y-1.5 text-[12px]">
              {log.map((l, i) => (
                <div key={i} className="flex items-center justify-between border-b border-border py-1.5">
                  <span className="capitalize font-medium">{l.action}</span>
                  <span className="text-muted-foreground">{l.user} · {new Date(l.timestamp).toLocaleString()}</span>
                </div>
              ))}
            </div>}
      </div>
    </div>
  );
}
